import { Trans as _Trans } from "../lib/i18n/Trans";
import { ChevronLeft, ChevronRight } from "../icons/v2-stroke";
import { cn } from "../lib/utils";
import React, { useCallback } from "react";
import { Button } from "./Button";
const pagesShownInControls = 7;
/**
 * A controlled pager for tabular or list data: it derives the page count from
 * `rowCount` and the current `pagination` state, and reports changes through
 * `setPagination`. Use it to page through a large dataset rendered in chunks,
 * e.g. a table backed by @tanstack/react-table.
 * @summary Controlled pager for tables and lists.
 */
/**
 * @cc [owner:aubin-tchoi,label:product] pagination-narrow-layout
 * Below 768px viewport width, the range summary MUST wrap below the page controls when
 * they do not fit on one line. Desktop layout MUST remain unchanged.
 */
export function Pagination({
  size = "sm",
  showDetails = true,
  showPageButtons = true,
  rowCount,
  rowCountIsCapped = false,
  pagination,
  setPagination,
  disablePaginationNumbers = false
}) {
  // pageIndex is 0-based
  const {
    pageIndex,
    pageSize
  } = pagination;
  const numPages = Math.ceil(rowCount / pageSize);
  const canNextPage = pagination.pageIndex < numPages - 1;
  const canPreviousPage = pageIndex > 0;
  const nextPage = () => setPagination({
    pageSize,
    pageIndex: pageIndex + 1
  });
  const previousPage = () => setPagination({
    pageSize,
    pageIndex: pageIndex - 1
  });
  const controlsAreHidden = Boolean(numPages <= 1);
  const firstItemOnPageIndex = pageIndex * pageSize + 1;
  const lastItemOnPageIndex = rowCount > (pageIndex + 1) * pageSize ? (pageIndex + 1) * pageSize : rowCount;
  const onPaginationButtonClick = useCallback(pageIndex => {
    setPagination({
      pageSize,
      pageIndex
    });
  }, [pageSize, setPagination]);
  const pageButtons = getPageButtons(pageIndex, numPages, pagesShownInControls, size, !disablePaginationNumbers ? onPaginationButtonClick : undefined);
  return /*#__PURE__*/React.createElement("div", {
    className: cn("flex w-full items-center max-md:flex-wrap max-md:gap-2", controlsAreHidden ? "justify-end" : "justify-between")
  }, /*#__PURE__*/React.createElement("div", {
    className: cn("flex max-md:shrink-0", controlsAreHidden ? "invisible" : "visible", showPageButtons ? "gap-0" : "gap-2")
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "outline",
    size: "xs",
    disabled: !canPreviousPage,
    icon: ChevronLeft,
    onClick: previousPage
  }), /*#__PURE__*/React.createElement("div", {
    className: cn("items-center", size === "xs" ? "gap-3 px-3" : "gap-4 px-4", showPageButtons ? "flex" : "hidden")
  }, pageButtons), /*#__PURE__*/React.createElement(Button, {
    variant: "outline",
    size: "xs",
    disabled: !canNextPage,
    icon: ChevronRight,
    onClick: nextPage
  })), /*#__PURE__*/React.createElement("span", {
    className: cn("text-xs", "text-muted-foreground", showDetails ? "visible" : "collapse")
  }, /*#__PURE__*/React.createElement(PaginationDetails, {
    controlsAreHidden: controlsAreHidden,
    rowCount: rowCount,
    rowCountIsCapped: rowCountIsCapped,
    firstItemOnPageIndex: firstItemOnPageIndex,
    lastItemOnPageIndex: lastItemOnPageIndex
  })));
}
function PaginationDetails({
  controlsAreHidden,
  rowCount,
  rowCountIsCapped,
  firstItemOnPageIndex,
  lastItemOnPageIndex
}) {
  if (controlsAreHidden) {
    return /*#__PURE__*/React.createElement(_Trans,
    /** i18n */
    {
      id: "xWNZJD",
      message: "{rowCount, plural, one {# item} other {# items}}",
      values: {
        rowCount: rowCount
      }
    });
  }
  if (rowCountIsCapped) {
    return /*#__PURE__*/React.createElement(_Trans,
    /** i18n */
    {
      id: "9NXElU",
      message: "Showing {firstItemOnPageIndex}-{lastItemOnPageIndex} of {rowCount, plural, one {#+ item} other {#+ items}}",
      values: {
        firstItemOnPageIndex: firstItemOnPageIndex,
        lastItemOnPageIndex: lastItemOnPageIndex,
        rowCount: rowCount
      }
    });
  }
  return /*#__PURE__*/React.createElement(_Trans,
  /** i18n */
  {
    id: "rBlGY2",
    message: "Showing {firstItemOnPageIndex}-{lastItemOnPageIndex} of {rowCount, plural, one {# item} other {# items}}",
    values: {
      firstItemOnPageIndex: firstItemOnPageIndex,
      lastItemOnPageIndex: lastItemOnPageIndex,
      rowCount: rowCount
    }
  });
}
function renderPageNumber(pageNumber, currentPage, size, onPageClick) {
  return /*#__PURE__*/React.createElement("button", {
    key: pageNumber,
    className: cn("font-medium transition-colors duration-200", currentPage === pageNumber ? "text-foreground" : "text-primary-400", size === "xs" ? "text-xs" : "text-sm"),
    onClick: () => onPageClick && onPageClick(pageNumber),
    disabled: !onPageClick
  }, pageNumber + 1);
}
function renderEllipses(size) {
  return /*#__PURE__*/React.createElement("span", {
    className: cn("text-sm font-medium", "text-muted-foreground", size === "xs" ? "text-xs" : "text-sm")
  }, "...");
}
function getPageButtons(currentPage, totalPages, slots, size, onPageClick) {
  const pagination = [];

  // If total pages are less than or equal to slots, show all pages
  if (totalPages <= slots) {
    for (let i = 0; i < totalPages; i++) {
      pagination.push(renderPageNumber(i, currentPage, size, onPageClick));
    }
    return pagination;
  }
  const remainingSlots = slots - 2; // slots excluding first and last page
  const halfSlots = Math.floor(remainingSlots / 2);

  // Ensure current page is within bounds
  currentPage = Math.max(0, Math.min(currentPage, totalPages - 1));
  pagination.push(renderPageNumber(0, currentPage, size, onPageClick)); // Always show the first page
  // Determine the range of pages to display
  let start, end;
  if (currentPage <= halfSlots + 1) {
    start = 1;
    end = remainingSlots - 1;
  } else if (currentPage >= totalPages - halfSlots - 2) {
    start = totalPages - remainingSlots;
    end = totalPages - 2;
  } else {
    start = currentPage - halfSlots + 1;
    end = currentPage + halfSlots - 1;
  }
  // Add ellipsis if there is a gap between the first page and the start of the range
  if (start > 1) {
    pagination.push(renderEllipses(size));
  }

  // Add the range of pages
  for (let i = start; i <= end; i++) {
    pagination.push(renderPageNumber(i, currentPage, size, onPageClick));
  }

  // Add ellipsis if there is a gap between the end of the range and the last page
  if (end < totalPages - 2) {
    pagination.push(renderEllipses(size));
  }
  pagination.push(renderPageNumber(totalPages - 1, currentPage, size, onPageClick)); // Always show the last page

  return pagination;
}
//# sourceMappingURL=Pagination.js.map